import { Box, Group, Paper, Select, SimpleGrid, Stack, Text, Button, Flex } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "@/context/UserContext";
import AppHeaderUserActions from "@/components/shared/AppHeaderUserActions";
import DashboardCard from "./DashboardCard";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import CollaborationTimelineCard, {
  CollaborationTimelineCardSkeleton,
  type CollaborationTimelineCardItem,
} from "./CollaborationTimelineCard";
import TitleComponent from "@/components/shared/TitleComponent";
import { useVerificationModal } from "@/components/layout/AppLayout";

export default function DashboardPage() {
  const navigate = useNavigate();
  const { userDetails, pendingRequestCounts, collaborationSidebarCounts, shortlistedIds } = useUserContext();
  const { openVerificationModal } = useVerificationModal();
  const userName = userDetails?.name?.trim() || "User";
  const [timelineItems, setTimelineItems] = useState<CollaborationTimelineCardItem[]>([]);
  const [selectedStage, setSelectedStage] = useState<string>("all");
  const [selectedProgress, setSelectedProgress] = useState<string>("pending");
  const [selectedScope, setSelectedScope] = useState<string>("your");

  const [loadingTimeline, setLoadingTimeline] = useState(true);
  const companyStatus = userDetails?.company?.status;
  const canShowVerifyButton = companyStatus !== "VERIFIED" && companyStatus !== "SYSTEM_VERIFIED";

  const filteredTimelineItems = useMemo(() => {
    if (selectedStage === "all") return timelineItems;

    const getStepDone = (item: CollaborationTimelineCardItem, side: "mySide" | "partnerSide"): boolean => {
      const sideSteps = item[side];
      if (selectedStage === "request") return sideSteps.request.done;
      if (selectedStage === "payment") return sideSteps.payment.done;
      if (selectedStage === "nda") return sideSteps.nda.done;
      return sideSteps.partnerReadiness.done;
    };

    return timelineItems.filter((item) => {
      const isDoneMySide = getStepDone(item, "mySide");
      const isDonePartnerSide = getStepDone(item, "partnerSide");
      const targetDone = selectedProgress === "completed";

      if (selectedScope === "your") return isDoneMySide === targetDone;
      if (selectedScope === "partner") return isDonePartnerSide === targetDone;
      return isDoneMySide === targetDone && isDonePartnerSide === targetDone;
    });
  }, [timelineItems, selectedStage, selectedProgress, selectedScope]);

  useEffect(() => {
    let mounted = true;

    const fetchTimeline = async () => {
      setLoadingTimeline(true);
      try {
        const data = await trpc.collaboration.getDashboardTimeline.query();
        if (!mounted) return;
        setTimelineItems((data || []) as CollaborationTimelineCardItem[]);
      } catch (err) {
        handleTrpcError(err);
        if (!mounted) return;
        setTimelineItems([]);
      } finally {
        if (mounted) setLoadingTimeline(false);
      }
    };

    if (userDetails?.company?.id) {
      fetchTimeline();
    } else {
      setLoadingTimeline(false);
      setTimelineItems([]);
    }

    return () => {
      mounted = false;
    };
  }, [userDetails?.company?.id]);

  return (
    <Box w="100%" maw="100%" style={{ minWidth: 0 }}>
      <TitleComponent
        title={`Welcome! ${userName}`}
        description="Here is your dashboard overview."
        rightSection={<AppHeaderUserActions />}
      />

      <Box p="lg">
        <Stack gap="lg">
          {canShowVerifyButton && (
            <Paper p="md" radius="md" withBorder>
              <Flex justify="space-between" align="center" gap="sm" wrap="wrap">
                <Box>
                  <Text fw={700}>Complete Company Verification</Text>
                  <Text size="sm" c="dimmed">
                    Verify your company details from dashboard. Credits are deducted only when verification is processed.
                  </Text>
                </Box>
                <Button onClick={openVerificationModal}>Verify Company</Button>
              </Flex>
            </Paper>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing="md">
            <Box>
              <DashboardCard
                title="Shortlist"
                count={shortlistedIds.size}
                description="Companies you have shortlisted"
                onClick={() => navigate("/shortlist")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Requests Sent"
                count={pendingRequestCounts.sent}
                description="Requests you've sent waiting for response"
                onClick={() => navigate("/requests")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Requests Received"
                count={pendingRequestCounts.received}
                description="Requests you've received from others"
                onClick={() => navigate("/requests")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Active Collaborations"
                count={collaborationSidebarCounts.active}
                description="Ongoing collaborations with partners"
                onClick={() => navigate("/collaborations")}
              />
            </Box>
            <Box>
              <DashboardCard
                title="Closed Collaborations"
                count={collaborationSidebarCounts.closed}
                description="Completed or inactive collaborations"
                onClick={() => navigate("/collaborations")}
              />
            </Box>
          </SimpleGrid>

          <Paper p="lg" radius="md" withBorder>
            <Group justify="space-between" align="center" mb="sm">
              <Box>
                <Text fw={700} fz="lg">
                  Collaboration Timeline
                </Text>
                <Text c="dimmed" size="sm">
                  Track the progress of your collaboration steps from both sides.
                </Text>
              </Box>
              <Group gap="lg">
                <Group gap={6}>
                  <Box w={10} h={10} style={{ borderRadius: "50%", backgroundColor: "var(--mantine-color-green-6)" }} />
                  <Text size="sm" c="dimmed">
                    Completed
                  </Text>
                </Group>
                <Group gap={6}>
                  <Box w={10} h={10} style={{ borderRadius: "50%", backgroundColor: "#E17100" }} />
                  <Text size="sm" c="dimmed">
                    Pending
                  </Text>
                </Group>
              </Group>
            </Group>

            <Group gap="sm" mb="sm" align="end">
              <Select
                label="Stage"
                data={[
                  { value: "all", label: "All Stages" },
                  { value: "request", label: "Request" },
                  { value: "payment", label: "Payment" },
                  { value: "nda", label: "NDA Sign" },
                  { value: "partnerReadiness", label: "Partner Readiness" },
                ]}
                value={selectedStage}
                onChange={(value) => setSelectedStage(value || "all")}
                w={190}
                size="xs"
              />
              <Select
                label="Progress"
                data={[
                  { value: "pending", label: "Pending" },
                  { value: "completed", label: "Completed" },
                ]}
                value={selectedProgress}
                onChange={(value) => setSelectedProgress(value || "pending")}
                w={140}
                size="xs"
              />
              <Select
                label="Scope"
                data={[
                  { value: "your", label: "Your Side" },
                  { value: "partner", label: "Partner Side" },
                  { value: "both", label: "Both Sides" },
                ]}
                value={selectedScope}
                onChange={(value) => setSelectedScope(value || "your")}
                w={150}
                size="xs"
              />
            </Group>

            {loadingTimeline ? (
              <Stack gap="sm">
                <CollaborationTimelineCardSkeleton />
              </Stack>
            ) : filteredTimelineItems.length === 0 ? (
              <Text size="sm" c="dimmed">
                No collaborations found for selected filters.
              </Text>
            ) : (
              <Stack gap="sm">
                {filteredTimelineItems.map((item) => (
                  <CollaborationTimelineCard key={item.matchId} item={item} />
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}
